import React, { useState, useEffect } from "react";
import {
    Plus,
    Pencil,
    Trash2,
    GripVertical,
    X,
} from "lucide-react";
import * as Icons from "lucide-react";
import * as api from "../../lib/api";
import { TICKET_TYPES } from "../../lib/utils";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "../ui/dialog";
import { PageLoader } from "../shared/LoadingSpinner";
import { toast } from "sonner";

const ICON_OPTIONS = [
    "Monitor", "Users", "Building2", "ShieldAlert", "HelpCircle",
    "Folder", "Server", "Wrench", "Briefcase", "Globe",
];

export function AdminTicketCategories() {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({
        name: "",
        icon: "Folder",
        description: "",
        ticket_types: [],
    });

    useEffect(() => {
        loadCategories();
    }, []);

    const loadCategories = async () => {
        try {
            setLoading(true);
            const data = await api.getTicketCategories();
            setCategories(data);
        } catch (err) {
            toast.error("Erreur lors du chargement");
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (cat = null) => {
        if (cat) {
            setEditing(cat);
            setForm({
                name: cat.name,
                icon: cat.icon,
                description: cat.description || "",
                ticket_types: cat.ticket_types || [],
            });
        } else {
            setEditing(null);
            setForm({ name: "", icon: "Folder", description: "", ticket_types: [] });
        }
        setDialogOpen(true);
    };

    const handleSave = async () => {
        try {
            if (editing) {
                await api.updateTicketCategory(editing.id, form);
                toast.success("Catégorie mise à jour");
            } else {
                await api.createTicketCategory(form);
                toast.success("Catégorie créée");
            }
            setDialogOpen(false);
            loadCategories();
        } catch (err) {
            toast.error("Erreur lors de la sauvegarde");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Supprimer cette catégorie ?")) return;
        try {
            await api.deleteTicketCategory(id);
            toast.success("Catégorie supprimée");
            loadCategories();
        } catch (err) {
            toast.error("Erreur lors de la suppression");
        }
    };

    const toggleType = (typeId) => {
        setForm((prev) => ({
            ...prev,
            ticket_types: prev.ticket_types.includes(typeId)
                ? prev.ticket_types.filter((t) => t !== typeId)
                : [...prev.ticket_types, typeId],
        }));
    };

    if (loading) return <PageLoader />;

    return (
        <div className="page-container" data-testid="admin-ticket-categories">
            <div className="page-header flex items-center justify-between">
                <div>
                    <h1 className="page-title">Catégories de tickets</h1>
                    <p className="page-subtitle">
                        Configurez les catégories affichées lors de la création d'un ticket
                    </p>
                </div>
                <Button onClick={() => handleOpenDialog()} data-testid="add-category-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter
                </Button>
            </div>

            <div className="space-y-3 mt-6">
                {categories.map((cat) => {
                    const Icon = Icons[cat.icon] || Icons.HelpCircle;
                    return (
                        <div
                            key={cat.id}
                            className="card"
                            data-testid={`tcat-row-${cat.id}`}
                        >
                            <div className="card-body flex items-center gap-4">
                                <GripVertical className="w-4 h-4 text-gray-300 flex-shrink-0" />
                                <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                                    <Icon className="w-5 h-5 text-gray-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className="text-sm font-semibold">{cat.name}</h3>
                                    <p className="text-xs text-gray-500">{cat.description}</p>
                                </div>
                                <div className="flex flex-wrap gap-1.5 flex-1 min-w-0">
                                    {(cat.ticket_types || []).map((typeId) => {
                                        const t = TICKET_TYPES.find((tt) => tt.id === typeId);
                                        return (
                                            <span
                                                key={typeId}
                                                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200"
                                            >
                                                {t?.label || typeId}
                                            </span>
                                        );
                                    })}
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleOpenDialog(cat)}
                                        data-testid={`edit-tcat-${cat.id}`}
                                    >
                                        <Pencil className="w-4 h-4" />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleDelete(cat.id)}
                                    >
                                        <Trash2 className="w-4 h-4 text-red-500" />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Edit/Create Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>
                            {editing ? "Modifier la catégorie" : "Nouvelle catégorie"}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-5 py-4">
                        <div>
                            <Label className="form-label">Nom *</Label>
                            <Input
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder="Ex: Informatique"
                                data-testid="tcat-name"
                            />
                        </div>
                        <div>
                            <Label className="form-label">Description</Label>
                            <Input
                                value={form.description}
                                onChange={(e) => setForm({ ...form, description: e.target.value })}
                                placeholder="Ex: Service informatique"
                                data-testid="tcat-description"
                            />
                        </div>
                        <div>
                            <Label className="form-label">Icône</Label>
                            <div className="flex flex-wrap gap-2">
                                {ICON_OPTIONS.map((iconName) => {
                                    const Ic = Icons[iconName] || Icons.HelpCircle;
                                    return (
                                        <button
                                            key={iconName}
                                            type="button"
                                            onClick={() => setForm({ ...form, icon: iconName })}
                                            className={`w-10 h-10 rounded-lg flex items-center justify-center border-2 transition-colors ${
                                                form.icon === iconName
                                                    ? "border-blue-500 bg-blue-50 text-blue-600"
                                                    : "border-gray-200 bg-gray-50 text-gray-500 hover:border-gray-300"
                                            }`}
                                            data-testid={`icon-${iconName}`}
                                        >
                                            <Ic className="w-5 h-5" />
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                        <div>
                            <Label className="form-label">Types de tickets associés</Label>
                            <div className="border rounded-md p-3 space-y-2 max-h-64 overflow-y-auto">
                                {TICKET_TYPES.map((type) => {
                                    const TypeIcon = Icons[type.icon] || Icons.HelpCircle;
                                    return (
                                        <label
                                            key={type.id}
                                            className="flex items-center gap-2 p-1.5 hover:bg-gray-50 rounded cursor-pointer"
                                        >
                                            <Checkbox
                                                checked={form.ticket_types.includes(type.id)}
                                                onCheckedChange={() => toggleType(type.id)}
                                                data-testid={`tcat-type-${type.id}`}
                                            />
                                            <TypeIcon className="w-4 h-4 text-gray-500" />
                                            <span className="text-sm">{type.label}</span>
                                            <span className="text-xs text-gray-400 ml-auto">
                                                {type.description}
                                            </span>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>
                            Annuler
                        </Button>
                        <Button
                            onClick={handleSave}
                            disabled={!form.name}
                            data-testid="save-tcat-btn"
                        >
                            {editing ? "Enregistrer" : "Créer"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
